import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const OpsOrgTrialSupport = ({ summary, loading, error, onRefresh }) => {
  const [saveLoading, setSaveLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [form, setForm] = useState({
    trial_end_date: '',
    unlimited_requests: false,
    api_rate_limit: '',
    api_src_limit: '',
    api_src_derived_flag: false,
    api_hist_limit: '0',
    excel_plugin_flag: false,
    sso_custom_idp_trial_override_flag: false,
  });

  useEffect(() => {
    setForm({
      trial_end_date: toDateInputValue(summary?.subscription?.trial_end),
      unlimited_requests: summary?.api_rate_limit == null,
      api_rate_limit: summary?.api_rate_limit == null ? '' : String(summary.api_rate_limit),
      api_src_limit: summary?.api_src_limit || '',
      api_src_derived_flag: Boolean(summary?.api_src_derived_flag),
      api_hist_limit: summary?.api_hist_limit == null ? '0' : String(summary.api_hist_limit),
      excel_plugin_flag: Boolean(summary?.excel_plugin_flag),
      sso_custom_idp_trial_override_flag: Boolean(summary?.sso_custom_idp_trial_override_flag),
    });
    setStatus({ type: '', message: '' });
  }, [
    summary?.org_id,
    summary?.subscription?.trial_end,
    summary?.api_rate_limit,
    summary?.api_src_limit,
    summary?.api_src_derived_flag,
    summary?.api_hist_limit,
    summary?.excel_plugin_flag,
    summary?.sso_custom_idp_trial_override_flag,
  ]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!summary?.org_id || saveLoading) return;
    setSaveLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${summary.org_id}/actions/update-trial-support`, {
        ...form,
        api_rate_limit: form.unlimited_requests ? null : form.api_rate_limit,
        api_hist_limit: form.api_hist_limit,
      });
      setStatus({ type: 'success', message: response.data?.message || 'Trial support settings updated.' });
      if (typeof onRefresh === 'function') {
        await Promise.resolve(onRefresh());
      }
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update trial support settings.' });
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <div className="text-muted">Loading trial support settings...</div>;
  if (error) return <div className="alert alert-danger" role="alert">{error}</div>;
  if (!summary) return null;

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">Trial Support</h5>
        {status.message && (
          <div className={`alert alert-${status.type}`} role="alert">
            {status.message}
          </div>
        )}
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label small text-muted">Trial Access Through</label>
            <input
              type="date"
              className="form-control"
              value={form.trial_end_date}
              onChange={(e) => handleChange('trial_end_date', e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small text-muted">Monthly Request Limit</label>
            <input
              type="number"
              min="0"
              step="1"
              className="form-control"
              value={form.api_rate_limit}
              onChange={(e) => handleChange('api_rate_limit', e.target.value)}
              disabled={form.unlimited_requests}
            />
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="form-check form-switch finbase-toggle mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="trial-unlimited-requests"
                checked={form.unlimited_requests}
                onChange={(e) => handleChange('unlimited_requests', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="trial-unlimited-requests">
                Unlimited Requests
              </label>
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label small text-muted">Dataset Availability</label>
            <input
              type="text"
              className="form-control"
              value={form.api_src_limit}
              onChange={(e) => handleChange('api_src_limit', e.target.value)}
              placeholder="Leave blank for all data sources"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted">Historic Availability</label>
            <input
              type="number"
              min="0"
              step="1"
              className="form-control"
              value={form.api_hist_limit}
              onChange={(e) => handleChange('api_hist_limit', e.target.value)}
            />
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <div className="form-check form-switch finbase-toggle mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="trial-derived-data"
                checked={form.api_src_derived_flag}
                onChange={(e) => handleChange('api_src_derived_flag', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="trial-derived-data">
                Derived Data
              </label>
            </div>
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <div className="form-check form-switch finbase-toggle mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="trial-excel-access"
                checked={form.excel_plugin_flag}
                onChange={(e) => handleChange('excel_plugin_flag', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="trial-excel-access">
                Excel Access
              </label>
            </div>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="form-check form-switch finbase-toggle mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="trial-sso-custom-idp"
                checked={form.sso_custom_idp_trial_override_flag}
                onChange={(e) => handleChange('sso_custom_idp_trial_override_flag', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="trial-sso-custom-idp">
                SSO / Custom IdP Trial Access
              </label>
            </div>
          </div>
          <div className="col-12">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? 'Saving...' : 'Save Trial Support Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsOrgTrialSupport;
